/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { createContext, useContext, useMemo } from 'react';
import PropTypes from "prop-types";
// Declaring as a static const for reference integrity/reducing rerenders
var emptyDefaults = {};

/*
 * Context
 */
export var EuiComponentDefaultsContext = /*#__PURE__*/createContext(emptyDefaults);

/*
 * Component
 */

export var EuiComponentDefaultsProvider = ({
  componentDefaults = emptyDefaults,
  children
}) => {
  return <EuiComponentDefaultsContext.Provider value={componentDefaults}>
      {children}
    </EuiComponentDefaultsContext.Provider>;
};

/*
 * Hooks
 */
EuiComponentDefaultsProvider.propTypes = {
  componentDefaults: PropTypes.shape({
    /**
       * Provide a global configuration for EuiPortal's default insertion position.
       */
    EuiPortal: PropTypes.any,
    /**
       * Provide a global configuration for EuiFocusTrap's `gapMode` and `crossFrame` props
       */
    EuiFocusTrap: PropTypes.any,
    /**
       * Provide global settings for EuiTablePagination's props that affect page size
       * / the rows per page selection.
       *
       * These defaults will be inherited all table and grid components that utilize EuiTablePagination.
       */
    EuiTablePagination: PropTypes.any,
    /**
       * Provide a global configuration for EuiTable's `responsiveBreakpoint` prop. Defaults to `'s'`.
       *
       * Defaults will be inherited by all `EuiBasicTable`s and `EuiInMemoryTable`s.
       */
    EuiTable: PropTypes.any
  })
};
export const useComponentDefaults = () => {
  return useContext(EuiComponentDefaultsContext);
};

// Merge individual component props with component defaults
export const usePropsWithComponentDefaults = (componentName, props) => {
  const context = useContext(EuiComponentDefaultsContext);
  const componentDefaults = context[componentName] ?? emptyDefaults;
  return useMemo(() => ({
    ...componentDefaults,
    ...props
  }), [componentDefaults, props]);
};
try {
  EuiComponentDefaultsProvider.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/provider/component_defaults/component_defaults.tsx',
    description: '',
    displayName: 'EuiComponentDefaultsProvider',
    methods: [],
    props: {
      componentDefaults: {
        defaultValue: {
          value: '{}'
        },
        description: '',
        name: 'componentDefaults',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/provider/component_defaults/component_defaults.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'EuiComponentDefaults'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}